<script setup lang="ts">
import {AlertDialogType, useAlertDialog} from "@/shared/components/ui/alert-dialog/use-alert-dialog.ts";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/shared/components/ui/alert-dialog/";
import Button from "@/shared/components/ui/button/Button.vue";
import {computed} from "vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {faCheckCircle, faExclamationTriangle, faXmarkCircle} from "@fortawesome/pro-solid-svg-icons";
import AlertDialogComponent from "@/shared/components/ui/alert-dialog/AlertDialogComponent.vue";
import {Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerClose} from "@/shared/components/ui/drawer";
import {useScreenSize} from "@/shared/composable/screensize.ts";

const {_alertDialog, _visible, closeAlertDialog, onConfirm, onCancel} = useAlertDialog()

const buttonVariant = computed(() => findButtonVariant(_alertDialog.value?.type))
const icon = computed(() => findIcon(_alertDialog.value?.type))

function findButtonVariant(type?: AlertDialogType) {
  switch (type) {
    case AlertDialogType.CONFIRMATION: return "default"
    case AlertDialogType.DESTRUCTIVE: return "destructive"
    default: return "outline"
  }
}

function findIcon(type?: AlertDialogType) {
  switch (type) {
    case AlertDialogType.SUCCESS: return { icon: faCheckCircle, color: 'text-green-500' };
    case AlertDialogType.WARNING: return { icon: faExclamationTriangle, color: 'text-yellow-500' };
    case AlertDialogType.ERROR: return { icon: faXmarkCircle, color: 'text-red-500' };
    case AlertDialogType.DESTRUCTIVE: return { icon: faXmarkCircle, color: 'text-red-500' };
    default: return null;
  }
}

const {isMobile, isTablet, isDesktop} = useScreenSize()
const toggleDrawer = (open) => {
  if(open == false) {
    closeAlertDialog()
  }
}
</script>

<template>

  <Drawer v-if="isMobile" :open="_visible" @update:open="toggleDrawer">
    <DrawerContent>
      <DrawerHeader>
        <div class="flex justify-center items-center gap-x-2">
          <FontAwesomeIcon v-if="icon" :icon="icon.icon" :class="icon.color" />
          <DrawerTitle>{{ _alertDialog?.title }}</DrawerTitle>
        </div>
        <DrawerDescription v-if="_alertDialog?.description" v-html="_alertDialog?.description"/>
      </DrawerHeader>
      <div  v-if="_alertDialog?.component"  class="p-default pt-0">
        <component ref="dialogComponent"
                   :is="_alertDialog?.component"
                   v-bind="_alertDialog?.props" />
      </div>
      <DrawerFooter class="gap-y-default">
        <Button @click="onConfirm" :variant="buttonVariant" data-tests="confirm">{{ _alertDialog?.confirmLabel }}</Button>
        <Button v-if="_alertDialog?.cancelLabel" variant="hollow" @click="onCancel" data-tests="cancel">{{ _alertDialog?.cancelLabel }}</Button>
      </DrawerFooter>
    </DrawerContent>
  </Drawer>

  <AlertDialog v-else :open="_visible">
    <AlertDialogContent >
      <div id="alert-dialog">
      <AlertDialogHeader>
        <div class="flex justify-start items-center gap-x-2">
          <FontAwesomeIcon v-if="icon" :icon="icon.icon" size="xl" :class="icon.color" />
          <AlertDialogTitle>{{ _alertDialog?.title }}</AlertDialogTitle>
        </div>
      </AlertDialogHeader>
      <AlertDialogDescription class="mb-default mt-1" >
        <span v-if="_alertDialog?.description" v-html="_alertDialog?.description" />
      </AlertDialogDescription>
      <AlertDialogComponent>
        <component v-if="_alertDialog?.component" ref="dialogComponent"
                   :is="_alertDialog?.component"
                   v-bind="_alertDialog?.props" />
      </AlertDialogComponent>
      <AlertDialogFooter>
        <Button v-if="_alertDialog?.cancelLabel" variant="hollow" @click="onCancel" data-tests="cancel">{{ _alertDialog?.cancelLabel }}</Button>
        <Button @click="onConfirm" :variant="buttonVariant" data-tests="confirm">{{ _alertDialog?.confirmLabel }}</Button>
      </AlertDialogFooter>
      </div>
    </AlertDialogContent>
  </AlertDialog>
</template>

<style scoped>

</style>